/**
 * Palette de couleurs pastel CHALEUREUSE haut de gamme, appliquée par employé.
 * Tons : miel, ambre, terracotta, bleu pétrole doux, vert sauge, prune, sable, olive...
 *
 * Chaque employé est mappé de façon DÉTERMINISTE (hash djb2 sur employee_id)
 * → toujours la même couleur, quel que soit le mois/année/tenant/écran.
 *
 * Utilisée dans :
 *  - WeeklyTimeline.js (cartes de mission du planning)
 *  - GestionEquipes.js (en-têtes employé de la vue Sous-traitance)
 *
 * Toutes les couleurs de fond sont assez claires pour que les textes noirs
 * restent parfaitement lisibles (from-*-50 → via-*-100).
 */

export const EMPLOYEE_COLOR_PALETTE = [
  // Miel / ambre chaud
  { name: 'miel',        bg: 'bg-gradient-to-br from-amber-50 via-amber-100 to-yellow-50',    border: 'border-amber-300',    hover: 'hover:text-amber-800',    solidBg: 'bg-amber-100',    solidText: 'text-amber-800',    solidBorder: 'border-amber-200' },
  // Terracotta / rouge brique doux
  { name: 'terracotta',  bg: 'bg-gradient-to-br from-orange-50 via-orange-100 to-red-50',     border: 'border-orange-300',   hover: 'hover:text-orange-800',   solidBg: 'bg-orange-100',   solidText: 'text-orange-800',   solidBorder: 'border-orange-200' },
  // Bleu pétrole doux
  { name: 'petrole',     bg: 'bg-gradient-to-br from-teal-50 via-teal-100 to-cyan-50',        border: 'border-teal-300',     hover: 'hover:text-teal-800',     solidBg: 'bg-teal-100',     solidText: 'text-teal-800',     solidBorder: 'border-teal-200' },
  // Vert sauge
  { name: 'sauge',       bg: 'bg-gradient-to-br from-emerald-50 via-emerald-100 to-green-50', border: 'border-emerald-300',  hover: 'hover:text-emerald-800',  solidBg: 'bg-emerald-100',  solidText: 'text-emerald-800',  solidBorder: 'border-emerald-200' },
  // Sable / beige
  { name: 'sable',       bg: 'bg-gradient-to-br from-yellow-50 via-yellow-100 to-amber-50',   border: 'border-yellow-300',   hover: 'hover:text-yellow-800',   solidBg: 'bg-yellow-100',   solidText: 'text-yellow-800',   solidBorder: 'border-yellow-200' },
  // Prune / lie de vin doux
  { name: 'prune',       bg: 'bg-gradient-to-br from-rose-50 via-rose-100 to-pink-50',        border: 'border-rose-300',     hover: 'hover:text-rose-800',     solidBg: 'bg-rose-100',     solidText: 'text-rose-800',     solidBorder: 'border-rose-200' },
  // Bleu ardoise doux
  { name: 'ardoise',     bg: 'bg-gradient-to-br from-slate-50 via-slate-100 to-blue-50',      border: 'border-slate-300',    hover: 'hover:text-slate-800',    solidBg: 'bg-slate-100',    solidText: 'text-slate-800',    solidBorder: 'border-slate-200' },
  // Olive
  { name: 'olive',       bg: 'bg-gradient-to-br from-lime-50 via-lime-100 to-green-50',       border: 'border-lime-300',     hover: 'hover:text-lime-800',     solidBg: 'bg-lime-100',     solidText: 'text-lime-800',     solidBorder: 'border-lime-200' },
  // Lavande / mauve
  { name: 'lavande',     bg: 'bg-gradient-to-br from-violet-50 via-violet-100 to-purple-50',  border: 'border-violet-300',   hover: 'hover:text-violet-800',   solidBg: 'bg-violet-100',   solidText: 'text-violet-800',   solidBorder: 'border-violet-200' },
  // Bleu ciel doux
  { name: 'ciel',        bg: 'bg-gradient-to-br from-sky-50 via-sky-100 to-blue-50',          border: 'border-sky-300',      hover: 'hover:text-sky-800',      solidBg: 'bg-sky-100',      solidText: 'text-sky-800',      solidBorder: 'border-sky-200' },
  // Corail
  { name: 'corail',      bg: 'bg-gradient-to-br from-red-50 via-red-100 to-rose-50',          border: 'border-red-300',      hover: 'hover:text-red-800',      solidBg: 'bg-red-100',      solidText: 'text-red-800',      solidBorder: 'border-red-200' },
  // Indigo doux
  { name: 'indigo',      bg: 'bg-gradient-to-br from-indigo-50 via-indigo-100 to-blue-50',    border: 'border-indigo-300',   hover: 'hover:text-indigo-800',   solidBg: 'bg-indigo-100',   solidText: 'text-indigo-800',   solidBorder: 'border-indigo-200' },
];

// Style pour missions DIVERS (sans logement)
export const DIVERS_COLOR = {
  name: 'divers',
  bg: 'bg-gradient-to-br from-rose-50 via-rose-100 to-rose-50',
  border: 'border-rose-300',
  hover: 'hover:text-rose-800',
  solidBg: 'bg-rose-100',
  solidText: 'text-rose-800',
  solidBorder: 'border-rose-200',
};

// Style pour missions non assignées
export const UNASSIGNED_COLOR = {
  name: 'unassigned',
  bg: 'bg-gradient-to-br from-gray-50 via-gray-100 to-slate-50',
  border: 'border-gray-300',
  hover: 'hover:text-gray-800',
  solidBg: 'bg-gray-100',
  solidText: 'text-gray-600',
  solidBorder: 'border-gray-200',
};

/**
 * Retourne les classes CSS pour un employé donné, de façon stable.
 * @param {string|null} employeeId - UUID de l'employé
 * @param {object} options - { hasHousing: bool } — mission DIVERS si hasHousing=false
 * @returns {object} - { bg, border, hover, solidBg, solidText, solidBorder, name }
 */
export function getEmployeeCardStyle(employeeId, { hasHousing = true } = {}) {
  if (!hasHousing) return DIVERS_COLOR;
  if (!employeeId) return UNASSIGNED_COLOR;

  // Hash déterministe djb2 sur l'UUID
  let hash = 5381;
  for (let i = 0; i < employeeId.length; i++) {
    hash = ((hash << 5) + hash) + employeeId.charCodeAt(i);
    hash = hash & hash;
  }
  const idx = Math.abs(hash) % EMPLOYEE_COLOR_PALETTE.length;
  return EMPLOYEE_COLOR_PALETTE[idx];
}
